<template>
  <div class="content">
    <v-toolbar flat>
      <v-app-bar-nav-icon><v-icon>mdi-widgets-outline</v-icon></v-app-bar-nav-icon>
      <v-toolbar-title>
        <span v-on="canViewParentFederation ? { click: redirectToParentFederation } : {}" v-bind:class="{ link: canViewParentFederation }">{{ federation.name }}</span>
        ->
        <span>{{ discipline.name }}</span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <!-- open edit dialog button -->
      <div v-if="canManageCurrentDiscipline">
        <v-btn icon fab @click="openEditDisciplineDialog(discipline)">
          <v-icon>edit</v-icon>
        </v-btn>
      </div>
    </v-toolbar>

    <!-- Edit discipline dialog -->
    <v-dialog v-model="editDisciplineDialog" max-width="500px">
      <v-card>
        <v-card-text>
          <v-form ref="disciplineForm">
            <v-container>
              <v-row>
                <v-col cols="12" sm="6" md="12">
                  <v-text-field
                    v-model="editedDiscipline.name"
                    label="Nom de la discipline"
                    :rules="[rules.required, rules.counter]"
                    counter
                    maxlength="60"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeEditDisciplineDialog()">Annuler</v-btn>
          <v-btn color="blue darken-1" text @click="submitForm()">Modifier</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-tabs v-model="tab">
      <v-tab to="disciplineManagers"><v-icon class="tab-icon">mdi-account-tie</v-icon> Gestionnaires</v-tab>
      <v-tab to="coaches"><v-icon class="tab-icon">mdi-whistle-outline</v-icon> Entraineurs</v-tab>
      <v-tab to="ExperienceLevel" class="blue-ENE"><v-icon class="tab-icon blue-ENE">mdi-format-list-bulleted</v-icon> Niveaux de pratique</v-tab>
      <v-tab to="WorkForm" class="blue-ENE"><v-icon class="tab-icon blue-ENE">mdi-format-list-bulleted</v-icon> Formes de travail</v-tab>
      <v-tab to="Skill" class="blue-ENE"><v-icon class="tab-icon blue-ENE">mdi-file-tree</v-icon> Compétences</v-tab>
      <v-tab to="draggables" class="blue-ENE"><v-icon class="tab-icon blue-ENE">mdi-drag-variant</v-icon> Modération des draggables</v-tab>
      <v-tab to="AgeGroup" class="green-GCE"><v-icon class="tab-icon green-GCE">mdi-format-list-bulleted</v-icon> Catégorie d’âge</v-tab>
      <v-tab to="PracticeType" class="green-GCE"><v-icon class="tab-icon green-GCE">mdi-format-list-bulleted</v-icon> Type de pratique</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" style="overflow: visible; display: block">
      <v-tab-item id="disciplineManagers">
        <disciplineManagers v-if="discipline" :disciplineId_prop="discipline.id" />
      </v-tab-item>

      <v-tab-item id="coaches">
        <disciplineCoaches v-if="discipline" :disciplineId_prop="discipline.id" />
      </v-tab-item>

      <v-tab-item id="ExperienceLevel">
        <experienceLevels v-if="discipline" :categoryFed="discipline.experienceLevels" :disciplineId_prop="discipline.id" />
      </v-tab-item>

      <v-tab-item id="WorkForm">
        <workForms v-if="discipline" :categoryFed="discipline.workForms" :disciplineId_prop="discipline.id" />
      </v-tab-item>

      <v-tab-item id="Skill">
        <skills v-if="discipline" :categoryFed="discipline.skills" :disciplineId_prop="discipline.id" />
      </v-tab-item>

      <v-tab-item id="draggables">
        <draggables v-if="discipline" :disciplineId_prop="discipline.id" :federationId_prop="federation.id" :moderation="true" />
      </v-tab-item>

      <v-tab-item id="AgeGroup">
        <ageGroups v-if="discipline" :categoryFed="discipline.ageGroups" :disciplineId_prop="discipline.id" />
      </v-tab-item>

      <v-tab-item id="PracticeType">
        <practiceTypes v-if="discipline" :categoryFed="discipline.practiceTypes" :disciplineId_prop="discipline.id" />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { VTabs } from "vuetify/lib";
import disciplineManagers from "./disciplineManagers.vue";
import disciplineCoaches from "./disciplineCoaches.vue";
import experienceLevels from "./experienceLevels";
import workForms from "./workForms";
import ageGroups from "./ageGroups";
import practiceTypes from "./practiceTypes";
import skills from "./skills";
import draggables from "./draggables";
import { getDisciplineAsync, updateDisciplineAsync, getIsManagerPartOfDisciplineAsync } from "@/api/disciplinesApi";
import { getFederationByIdAsync, getIsUserPartOfFederationAsync } from "@/api/federationsApi";
import PermissionsService from "@/services/PermissionsService";
import AuthService from "@/services/AuthService";
import _ from "lodash";
import store from "@/store";

export default {
  components: {
    VTabs,
    disciplineManagers,
    disciplineCoaches,
    draggables,
    experienceLevels,
    workForms,
    ageGroups,
    practiceTypes,
    skills,
  },

  props: {
    disciplineId_prop: [Number, String],
    federationId_prop: [Number, String],
  },

  watch: {
    $route() {
      this.initPage();
    },
  },

  data() {
    return {
      disciplineId: null,
      federationId: null,
      authService: AuthService,
      isCurrentUserPartOfFederation: false,
      isCurrentUserManagerOfDiscipline: false,
      federation: {},
      tab: null,
      discipline: {},
      intFederationId: null,
      editedDiscipline: {},
      editDisciplineDialog: false,
      rules: {
        required: (value) => !!value || "Requis.",
        counter: (value) => (value ? value.length <= 60 : "Max 60 caractères"),
      },
    };
  },

  computed: {
    canViewCurrentDiscipline() {
      // Can view own discipline and is part of current discipline
      let canViewOwnDisciplineAndIsPartOfCurrentDiscipline =
        PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserManagerOfDiscipline;
      // Can view all Disciplines
      let canViewAllDisciplines = PermissionsService.DisciplinesPermissionsService.canViewAllDisciplinesOwnFederation;
      return canViewOwnDisciplineAndIsPartOfCurrentDiscipline || canViewAllDisciplines;
    },
    canManageCurrentDiscipline() {
      // Can manage own discipline and is part of current discipline
      let canManageCurrentDisciplineAndIsPartOfCurrentDiscipline =
        PermissionsService.DisciplinesPermissionsService.canManageOwnDiscipline && this.isCurrentUserManagerOfDiscipline;
      // Can manage all disciplines of the current federation
      let canManageAllDisciplines = PermissionsService.DisciplinesPermissionsService.canManageAllDisciplinesOwnFederation && this.isCurrentUserPartOfFederation;
      return canManageCurrentDisciplineAndIsPartOfCurrentDiscipline || canManageAllDisciplines;
    },
    canViewParentFederation() {
      // Can view own federation and is part of current federation
      let canViewOwnFederationAndIsPartOfCurrentFederation = PermissionsService.FederationsPermissionsService.canViewOwnFederation && this.isCurrentUserPartOfFederation;
      // Can view all federations
      let canViewAllFederations = PermissionsService.FederationsPermissionsService.canViewAllFederations;
      return canViewOwnFederationAndIsPartOfCurrentFederation || canViewAllFederations;
    },
  },

  async mounted() {
    await this.initPage();
  },

  methods: {
    async initPage() {
      // Override the props values from the url if provided
      this.federationId = this.$route.params.federationId ? parseInt(this.$route.params.federationId) : this.federationId_prop;
      this.disciplineId = this.$route.params.disciplineId ? parseInt(this.$route.params.disciplineId) : this.disciplineId_prop;

      // Check if the current user is part of current federation
      this.isCurrentUserPartOfFederation = await getIsUserPartOfFederationAsync(this.authService.userId, this.federationId);
      // Check if the current user is part of current discipline
      this.isCurrentUserManagerOfDiscipline = await getIsManagerPartOfDisciplineAsync(this.authService.userId, this.disciplineId);

      // Check if the user can see this page
      if (!this.canViewCurrentDiscipline) {
        store.dispatch("setSnackbar", {
          title: `Non Autorisé`,
          text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
          status: "warning",
          timeout: 2000,
        });

        this.$router.replace("/");
      }

      this.discipline = await getDisciplineAsync(this.disciplineId);
      this.federation = await getFederationByIdAsync(this.discipline.parentFederationId);

      // if federation id is string we wanna change it to int
      this.intFederationId = typeof this.federationId === "string" || this.federationId instanceof String ? parseInt(this.federationId) : this.federationId;
    },

    // Open edit discipline dialog, and clone current discipline to edit
    openEditDisciplineDialog(discipline) {
      this.editedDiscipline = _.cloneDeep(discipline);
      this.editDisciplineDialog = true;
    },

    // Close edit discipline dialog and clean cloned discipline
    closeEditDisciplineDialog() {
      this.editedDiscipline = {};
      this.editDisciplineDialog = false;
    },

    // UPDATE a discipline
    async submitForm() {
      // Validate the form
      var isFormValid = this.$refs.disciplineForm.validate();

      // If the form is valid
      if (isFormValid) {
        var result = await updateDisciplineAsync(this.editedDiscipline.id, this.editedDiscipline);

        // If we have an id > 0 in the result, the save succeeded
        if (result && result.id > 0) {
          store.dispatch("setSnackbar", {
            title: `Discipline mise à jour`,
            text: `La discipline a bien été mise à jour.`,
            status: "success",
            timeout: 2000,
          });

          this.editedDiscipline = {};
          this.$refs.disciplineForm.reset();
        }

        this.closeEditDisciplineDialog();

        this.discipline = await getDisciplineAsync(this.disciplineId);
      }
      // Else display the error messages
      else {
        // Handle missing name
        if (!this.editedDiscipline.name) {
          store.dispatch("setSnackbar", {
            title: "Nom requis",
            text: "Veuillez renseigner le nom.",
            status: "error",
            timeout: 2000,
          });
        }
      }
    },

    // Redirect to parent federation
    redirectToParentFederation() {
      this.$router.push({
        name: "FederationPanel",
        params: {
          id: this.federation.id,
          tab: "users",
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.blue-ENE {
  color: rgb(15, 21, 71) !important;
}
.green-GCE {
  color: rgb(27, 138, 98) !important;
}
.tab-icon {
  padding-right: 5px;
}
.link {
  color: rgb(15, 21, 71);
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-color: rgb(15, 21, 71);
  cursor: pointer;

  &:hover {
    color: rgba(15, 21, 71, 0.786);
    text-decoration-color: rgba(15, 21, 71, 0.5);
  }
  &:focus {
    color: rgba(15, 21, 71, 0.786);
    text-decoration-color: rgb(15, 21, 71, 0.5);
  }
  &:active {
    color: rgba(15, 21, 71, 0.786);
    text-decoration-color: rgb(15, 21, 71, 0.5);
  }
}
</style>
